import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Card, ExplanationBlock } from './data.service';

@Injectable({ providedIn: 'root' })
export class OcrJsonLoaderService {
  private readonly ASSET_URL = 'assets/ocr-questions/_all_questions.json';
  private readonly FIGURE_BASE = 'assets/ocr-questions/figures/';

  private readonly EXAM_YEAR_MAP: { pattern: RegExp; field: keyof Card }[] = [
    { pattern: /I\.A\.S\.|UPSC|Civil Services/i, field: 'yearIAS' },
    { pattern: /U\.P\.|UPPSC|U\.P\.R\.O\.|U\.P\.P\.C\.S\./i, field: 'yearUPPSC' },
    { pattern: /B\.P\.S\.C\.|BPSC|Bihar/i, field: 'yearBPSC' },
    { pattern: /M\.P\.|MPPCS|M\.P\.P\.C\.S\./i, field: 'yearMPPSC' },
    { pattern: /R\.A\.S\.|R\.T\.S\.|RPSC|Rajasthan/i, field: 'yearRPSC' },
    { pattern: /Uttarakhand|UKPSC/i, field: 'yearUKPSC' },
    { pattern: /Chhattisgarh|CGPSC/i, field: 'yearCGPSC' },
    { pattern: /Jharkhand|JPSC|J\.P\.C\.S\./i, field: 'yearJPSC' },
  ];

  constructor(private http: HttpClient) {}

  loadOcrQuestions(): Observable<Card[]> {
    return this.http.get<any[]>(this.ASSET_URL).pipe(
      map(items => items.map(item => this.mapToCard(item)))
    );
  }

  private extractFilename(absPath: string): string {
    if (!absPath) return '';
    return absPath.replace(/\\/g, '/').split('/').pop() || '';
  }

  private mapToCard(item: any): Card {
    const opts = item.options || {};
    const answer: string = (item.answer || '').toLowerCase();
    const answerOptionText = opts[answer] || '';
    const answerText = answer ? `(${answer}) ${answerOptionText}` : '';

    const yearFields: Partial<Card> = {};
    const examName: string = item.exam_name || '';
    const examYear: string = item.exam_year || '';
    for (const mapping of this.EXAM_YEAR_MAP) {
      if (mapping.pattern.test(examName)) {
        (yearFields as any)[mapping.field] = examYear;
        break;
      }
    }

    // Process question_blocks → first figure becomes the question figure
    let questionFigureUrl = '';
    let questionFigureCaption = '';
    const qBlocks: any[] = item.question_blocks || [];
    const qFigure = qBlocks.find((b: any) => b.type === 'figure');
    if (qFigure?.image_ref) {
      questionFigureUrl = this.FIGURE_BASE + this.extractFilename(qFigure.image_ref);
      questionFigureCaption = qFigure.content || '';
    }

    // Process explanation_blocks into typed ExplanationBlock[]
    const explanationBlocks: ExplanationBlock[] = (item.explanation_blocks || []).map((b: any) => ({
      type: b.type as 'text' | 'table' | 'figure',
      content: b.content || '',
      headers: b.headers || [],
      rows: b.rows || [],
      imageUrl: b.type === 'figure' && b.image_ref
        ? this.FIGURE_BASE + this.extractFilename(b.image_ref)
        : undefined,
    }));

    return {
      id: item.global_question_number,
      questionText: item.question_text || '',
      imageUrl: '',
      answerText,
      answerImageUrl: '',
      explanationText: item.explanation || '',
      explanationImageUrl: '',
      collapsed: false,
      questionNumber: item.question_number,
      optionAText: opts['a'] || '',
      optionBText: opts['b'] || '',
      optionCText: opts['c'] || '',
      optionDText: opts['d'] || '',
      questionFigureUrl,
      questionFigureCaption,
      explanationBlocks,
      examLabel: examYear && examName ? `${examName} ${examYear}` : (examName || examYear),
      ...yearFields,
    };
  }
}
